import { Component, ElementRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    CameraModalComponent,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    isDefined,
    ModalClass,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { BsModalService } from 'ngx-bootstrap/modal';
import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'pace-forms-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FileUploadComponent,
            multi: true,
        },
    ],
    standalone: false,
    host: {
        '(change)': 'emitFiles($event.target.files)',
    },
})
export class FileUploadComponent implements OnInit, ControlValueAccessor, OnDestroy {
    @Input() public allowedMimeTypes: string[];

    public onChange: (file: File | undefined) => void;
    public file: File | undefined;
    public fileOver = false;
    public thumbnail: string | undefined;
    private SUPPORTED_THUMBNAIL_TYPES = ['image/png', 'image/tiff', 'image/tiff-fx', 'image/jpeg'];

    private dragOver$: Observable<DragEvent>;
    private dragLeave$: Observable<DragEvent>;
    private drop$: Observable<DragEvent>;

    private onDestroy$: Subject<void> = new Subject();
    public constructor(
        private host: ElementRef<HTMLInputElement>,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        private readonly modalService: BsModalService,
    ) {
        this.dragOver$ = fromEvent<DragEvent>(this.host.nativeElement, 'dragover');
        this.dragLeave$ = fromEvent<DragEvent>(this.host.nativeElement, 'dragleave');
        this.drop$ = fromEvent<DragEvent>(this.host.nativeElement, 'drop');
    }

    public ngOnInit(): void {
        merge(
            this.dragOver$.pipe(
                tap((event) => {
                    if (!event.dataTransfer) {
                        return;
                    }
                    const file = event.dataTransfer.files?.item(0) ?? undefined;
                    if (isDefined(file) && !this.allowedMimeTypes.includes(file.type)) {
                        event.dataTransfer.dropEffect = 'none';
                        this.fileOver = false;
                    } else {
                        event.dataTransfer.dropEffect = 'copy';
                        this.fileOver = true;
                    }
                }),
            ),
            this.dragLeave$.pipe(
                tap(() => {
                    this.fileOver = false;
                }),
            ),
            this.drop$.pipe(
                tap((event) => {
                    this.fileOver = false;
                    this.emitFiles(event.dataTransfer?.files);
                }),
            ),
        )
            .pipe(
                tap((event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }),
                takeUntil(this.onDestroy$),
            )
            .subscribe();
    }

    public emitFiles(fileList: FileList | undefined): void {
        const file = fileList?.item(0) ?? undefined;
        this.onChange(file);
        this.file = file;
        if (isDefined(this.file) && this.SUPPORTED_THUMBNAIL_TYPES.includes(this.file.type)) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                this.thumbnail = typeof reader.result === 'string' ? reader.result : undefined;
            });
            reader.readAsDataURL(this.file);
        } else {
            this.thumbnail = undefined;
        }
    }

    public writeValue(): void {
        this.host.nativeElement.value = '';
        this.file = undefined;
    }

    public registerOnChange(fn: (file: File | undefined) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(): void {
        // empty
    }

    public hasImageMimeType(): boolean {
        return this.allowedMimeTypes.some((type) => type.startsWith('image/'));
    }

    public openCamera(): void {
        const modalInstance = this.modalService.show(CameraModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
        });
        modalInstance.content?.photoCaptured.pipe(takeUntil(this.onDestroy$), take(1)).subscribe((file: File) => {
            this.onChange(file);
            this.file = file;
            this.removeThumbnail();
            this.thumbnail = URL.createObjectURL(file);
        });
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    private removeThumbnail(): void {
        if (this.thumbnail?.startsWith('blob:')) {
            URL.revokeObjectURL(this.thumbnail);
        }
    }
}
